import React from 'react';

import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';

import { ZoneStatusBadge } from '../../components/OccupancyBadge';
import { fmtQty } from '../../format';
import { fmtAmount, fmtPct } from '../../occupancy';
import type {
  LocationUtilizationAttention,
  Pagination,
  UtilizationBasis,
  ZoneOccupancyThresholds,
} from '../../types';

/**
 * What to do about a location that is (almost) full: the server suggests the
 * storage location with the most room left (below the warn threshold).
 */
export const moveSuggestion = (
  a: LocationUtilizationAttention,
  basis: UtilizationBasis
): string =>
  a.moveTo
    ? `Pindahkan ke ${a.moveTo.name}: ${fmtAmount(a.moveTo.free, basis)} tersisa`
    : 'Belum ada lokasi lain dengan ruang tersisa';

/** Rows per server page of "Perlu tindakan" — the strip stays a strip. */
export const ATTENTION_LIMIT = 5;

/**
 * "Perlu tindakan": one server page of the storage locations at or over the
 * warn threshold on the chosen basis (fullest first), with where to move
 * stock to, and Sebelumnya / Berikutnya under it. Nothing to act on →
 * nothing rendered.
 */
export const AttentionStrip: React.FC<{
  items: LocationUtilizationAttention[];
  pagination: Pagination;
  thresholds: ZoneOccupancyThresholds;
  basis: UtilizationBasis;
  /** Another page is on its way (the current one stays, dimmed). */
  loading: boolean;
  onPage: (page: number) => void;
}> = ({ items, pagination: p, thresholds, basis, loading, onPage }) => {
  if (p.total === 0) return null;
  const from = Math.min(p.total, (p.page - 1) * p.size + 1);
  const to = Math.min(p.total, p.page * p.size);
  return (
    <Card padding="none" data-testid="occ-attention">
      <div className="border-b border-stroke-soft-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <i
            className="ri-error-warning-line text-[16px] text-(--state-warning-base)"
            aria-hidden
          />
          <h2 className="text-base font-bold tracking-tight text-text-strong-950">
            Perlu tindakan
          </h2>
          <span
            className="text-[12px] tabular-nums text-text-sub-600"
            data-testid="occ-attention-count"
          >
            {fmtQty(p.total)} lokasi
          </span>
        </div>
        <p
          className="mt-0.5 text-[12px] text-text-sub-600"
          data-testid="occ-attention-hint"
        >
          Lokasi yang sudah ≥ {thresholds.warn} % terpakai. Pindahkan sebagian
          stok supaya barang masuk tetap punya tempat.
        </p>
      </div>
      <ul
        className={`divide-y divide-stroke-soft-200 transition-opacity ${loading ? 'opacity-60' : ''}`}
        aria-busy={loading ? 'true' : undefined}
        aria-label="Lokasi perlu tindakan, paling penuh dulu"
      >
        {items.map((a) => (
          <li
            key={a.storageID}
            className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            data-testid={`occ-attention-${a.storageID}`}
          >
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="font-semibold text-text-strong-950">
                  {a.name}
                </span>{' '}
                <span className="tabular-nums text-text-sub-600">
                  · {fmtPct(a.pct)} terpakai · {fmtAmount(a.free, basis)}{' '}
                  tersisa
                </span>
                <ZoneStatusBadge
                  status={a.status}
                  thresholds={thresholds}
                  basis={basis}
                  testId={`occ-attention-status-${a.storageID}`}
                />
              </p>
            </div>
            <p className="flex items-center gap-1.5 text-[12px] text-text-sub-600 sm:text-right">
              <i className="ri-arrow-right-line text-[14px]" aria-hidden />
              {moveSuggestion(a, basis)}
            </p>
          </li>
        ))}
      </ul>
      <div
        className="flex flex-wrap items-center justify-between gap-2 border-t border-stroke-soft-200 px-4 py-2 text-[12px] text-text-sub-600"
        data-testid="occ-attention-pager"
      >
        <span className="tabular-nums" data-testid="occ-attention-range">
          {from}–{to} dari {fmtQty(p.total)} lokasi
        </span>
        <div className="flex items-center gap-1">
          <Button
            tone="neutral"
            style="stroke"
            size="xs"
            disabled={p.page <= 1 || loading}
            onClick={() => onPage(p.page - 1)}
            data-testid="occ-attention-prev"
          >
            Sebelumnya
          </Button>
          <Button
            tone="neutral"
            style="stroke"
            size="xs"
            disabled={p.page >= p.lastPage || loading}
            onClick={() => onPage(p.page + 1)}
            data-testid="occ-attention-next"
          >
            Berikutnya
          </Button>
        </div>
      </div>
    </Card>
  );
};
